import type { Theme } from 'core'
import type { IContactFormSettings } from 'core/store/models'
import type { RecorderError } from 'ui'
import type { AudioBlob } from 'vocal-recorder'
import { animateDOM, uploadFiles, useMemoryState } from 'core'
import { MicManager, RecorderErrors } from 'ui'
import z from 'zod'
import { useFormTheme } from './state.preview'

type ContactFormData = IContactFormSettings & {
  theme: Theme
}

export interface FormProps {
  /**
   * ID according to form types
   * - `contact` - account id of user
   * - `collect` - uid of collect form
   */
  id: string | number

  /** ID of vocal audio to reply to */
  replyId?: string

  /** Type of form */
  type: 'contact' | 'collect'

  /** Toggles minimal layout */
  minimal?: boolean

  /** Prefills email input */
  email?: string | null

  /**
   * State is peristed even after component is unmounted.
   * Does not persist after refresh/unload
   */
  persistState?: boolean

  // Telemetry
  origin?: string
  referrer?: string
}

export const FormResult = z.object({
  email: z.string().email(),
  message: z.coerce.string().nullable(),
  audio: z.custom<AudioBlob>().nullable()
})
  .refine(data => !!data.message || !!data.audio, {
    message: 'Either message or audio must be provided'
  })
  .transform((data) => {
    const { email, audio, message } = data

    const payload: [string, string, Blob] = audio
      ? ['recording', 'recording.mp3', audio]
      : ['message', 'message.txt', new Blob([message || ''])]

    return { email, payload }
  })

export const useForm = createSharedComposable((props?: FormProps) => {
  if (!props)
    throw new Error('First useForm() call must be initiated with props')

  const { id, type } = props

  // Validate props
  if (!id || !type)
    throw new Error('Valid props were not given. Make sure id and type are provided')

  // Services
  const mic = MicManager.use()
  const preview = useFormTheme()

  const state = useMemoryState(
    `form-state-${props.id}`,
    {
      /** Whether user data has loaded */
      isReady: false,

      /** Whether recording process has started  */
      hasStarted: false,

      error: undefined as RecorderError | undefined,

      // Result states
      email: props.email || '',
      message: '',
      audio: undefined as AudioBlob | void
    },
    { dispose: !props.persistState }
  )

  /** Starts recording flow */
  function start() {
    animateDOM(() => {
      reset(false)
      state.hasStarted = true
    })
  }

  /** Reset state */
  function reset(animate = true) {
    function clear() {
      state.error = undefined
      state.audio = undefined
      state.hasStarted = false
      state.message = ''
    }

    return animate ? animateDOM(clear) : clear()
  }

  /** Sync user theme/strings data */
  async function sync() {
    let data: ContactFormData | undefined

    // Get user contact form theme
    if (type === 'contact') {
      data = await http.static
        .get(http.getURL(`contact/${id}`))
        .json<ContactFormData>()
        .catch(() => undefined)
    }

    preview.update(data)
    state.isReady = true
  }

  async function upload() {
    try {
      const { email, payload } = FormResult.parse(state)
      const url = new URL(`${type}/${id}`, http.baseURL)

      /** Query params to pass */
      const query: Record<string, string> = {
        email,
        origin: props?.origin || location.origin,
        referrer: props?.referrer || document.referrer
      }

      if (props?.replyId)
        query['reply-audio-id'] = props.replyId

      // Assign query params
      for (const key in query)
        url.searchParams.set(key, query[key])

      const success = await uploadFiles(url.href, payload)
      track('Contact form uploaded', { success, type, ...query })

      return success
    }
    catch (error) {
      ErrorService.log(error)
      state.error = RecorderErrors.UploadFailed
    }
  }

  function download() {
    const result = state.audio

    if (!result)
      return

    const name = result.getFilename(`Vocal Recording - ${new Date().toLocaleString()}`)
    downloadFile(result, name)
  }

  onBeforeMount(() => {
    Logger.debug('Vocal form initiated', props)
    track('Contact form loaded', props)
    sync()
  })

  onBeforeUnmount(() => {
    if (!props.persistState)
      reset()
  })

  return {
    id,
    type,
    mic,

    props,
    state,
    start,
    reset,
    upload,
    download,

    get strings() {
      return preview.strings
    }
  }
})
